import React, { createContext, useState, useEffect, useCallback } from 'react';
import { auth } from "./firebase";
import { signOut } from "firebase/auth";
import {signInWithEmailAndPassword} from "firebase/auth";
import { getAuth } from "firebase/auth";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(() => {
    const savedData = localStorage.getItem("userData");
    return savedData ? JSON.parse(savedData) : null;
  });

  const [ranking, setRanking] = useState(() => {
    const savedRanking = localStorage.getItem("ranking");
    return savedRanking ? JSON.parse(savedRanking) : [];
  });

  const [statsQuizz, setStatsQuizz] = useState(() => {
    const savedStatsQuizz = localStorage.getItem("statsQuizz");
    return savedStatsQuizz ? JSON.parse(savedStatsQuizz) : {};
  });

  const [statsJour, setStatsJour] = useState(() => {
    const savedStatsJour = localStorage.getItem("statsJour");
    return savedStatsJour ? JSON.parse(savedStatsJour) : {};
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Enregistre la dernière interaction
  useEffect(() => {
    const recordLastInteraction = () => {
      localStorage.setItem("lastInteraction", Date.now());
    };

    // Écouter les interactions utilisateur
    window.addEventListener("click", recordLastInteraction);
    window.addEventListener("keypress", recordLastInteraction);

    return () => {
      // Nettoyer les écouteurs lors du démontage
      window.removeEventListener("click", recordLastInteraction);
      window.removeEventListener("keypress", recordLastInteraction);
    };
  }, []);

  // Vérifier l'inactivité au chargement de la page
  useEffect(() => {
    const lastInteraction = localStorage.getItem("lastInteraction");
    if (lastInteraction) {
      const now = Date.now();
      const timeSinceLastInteraction = now - parseInt(lastInteraction, 10);

      // Si l'inactivité dépasse 60 minutes (ou autre valeur souhaitée), déconnecte l'utilisateur
      if (timeSinceLastInteraction > 60 * 60 * 1000) {
        console.log("Session expirée en raison de l'inactivité.");
        logoutUser();
      }
    }
  }, []);

  // Vérifie l'expiration de la session périodiquement
  useEffect(() => {
    const interval = setInterval(() => {
      checkSessionExpiration(logoutUser);
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);
  

// Fonction de connexion
const loginUser = async (email, password) => {
  try {
     // Authentifier l'utilisateur avec Firebase Auth
     await signInWithEmailAndPassword(auth, email, password);

     // Obtenir le token ID pour l'authentification auprès de l'API
     const idToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);

    const response = await fetch("https://api-secret-forsides.forsides-group.com/api/getUserData", {
    // const response = await fetch("http://127.0.0.1:5000/api/getUserData", {
      method: "GET",
      headers: { 
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
       },
    });

    if (!response.ok) {
      const errorData = await response.json();

      // const token = response.token
      // Appel à l'API set-session-expiration
      // await fetch("http://localhost:5000/api/set-session-expiration", {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${token}`,
      //   },
      // });
      throw new Error(errorData.error || "Erreur lors de la connexion.");
    }

    const { user_data, ranking, stats_quizz, stats_jour } = await response.json();

    // Mise à jour du contexte et du localStorage
    setUserData(user_data);
    localStorage.setItem("userData", JSON.stringify(user_data));

    setRanking(ranking);
    localStorage.setItem("ranking", JSON.stringify(ranking));

    setStatsQuizz(stats_quizz);
    localStorage.setItem("statsQuizz", JSON.stringify(stats_quizz));

    setStatsJour(stats_jour);
    localStorage.setItem("statsJour", JSON.stringify(stats_jour));

    setIsAuthenticated(true);
  } catch (error) {
    console.error("Erreur lors de la connexion :", error);
    throw error;
  }
};

// Fonction de déconnexion
const logoutUser = async () => {
  try {
    await signOut(auth); // Déconnecte l'utilisateur côté Firebase
    console.log("Déconnexion réussie");

    // Supprimer les données utilisateur dans le contexte
    setUserData(null);

    // Nettoyer le localStorage
    localStorage.removeItem("userData");
    localStorage.removeItem("token");
    localStorage.removeItem("ranking");
    localStorage.removeItem("statsQuizz");
    localStorage.removeItem("statsJour");
    localStorage.removeItem("sessionExpiration");
  } catch (error) {
    console.error("Erreur lors de la déconnexion :", error);
    throw new Error("Impossible de déconnecter l'utilisateur.");
  }
};

// Fonction d'ouverture de case
const openCase = async (caseNumber) => {
  try {
    // const token = localStorage.getItem("token");
    const token = await auth.currentUser.getIdToken();
    console.log("le numéro envoyé : ", typeof(caseNumber))
    const response = await fetch("https://api-secret-forsides.forsides-group.com/api/open-case", {
    // const response = await fetch("http://127.0.0.1:5000/api/open-case", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ case_number: Number(caseNumber) }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erreur lors de l'ouverture de la case.");
    }

    const { case_opened, is_winner, points_earned, ranking: updatedRanking } =
      await response.json();

    // Mettre à jour les données utilisateur et le contexte
    setUserData((prevData) => ({
      ...prevData,
      cases_opened: case_opened,
      points: prevData.points + points_earned,
    }));
    localStorage.setItem("userData", JSON.stringify(userData));

    if (updatedRanking !== []) {
      setRanking(updatedRanking);
      localStorage.setItem("ranking", JSON.stringify(updatedRanking));
    }

    return { is_winner, points_earned };
  } catch (error) {
    console.error("Erreur lors de l'ouverture de la case :", error);
    throw error;
  }
};

const checkSessionExpiration = async (onLogout) => {
  const auth = getAuth();

  if (auth.currentUser) {
    try {
      // Récupérer le token ID actuel
      const idTokenResult = await auth.currentUser.getIdTokenResult(true);
      const expirationTimestamp = idTokenResult.claims.exp; // Date d'expiration en timestamp

      // Comparer avec l'heure actuelle
      const now = Math.floor(Date.now() / 1000); // En secondes
      if (now > expirationTimestamp) {
        console.log("Session expirée, déconnexion...");
        onLogout();
      }
    } catch (error) {
      console.error("Erreur lors de la vérification de l'expiration :", error);
      onLogout(); // Déconnecte en cas d'erreur
    }
  }
};

useEffect(() => {
  const interval = setInterval(() => {
    checkSessionExpiration(logoutUser);
  }, 5 * 60 * 1000); // Vérifie toutes les 5 minutes

  return () => clearInterval(interval); // Nettoie l'intervalle
}, []);

// Fonction pour mettre à jour quizz_answers et points
const updateUserDataQuizz = async (dayQuizzUserData, newPoints, day) => {
  try {
    const idToken = await auth.currentUser.getIdToken(true); // Obtenir le token Firebase

    const response = await fetch("https://api-secret-forsides.forsides-group.com/api/update-quizz", {
    // const response = await fetch("http://127.0.0.1:5000/api/update-quizz", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        dayQuizzUserData,
        newPoints,
        day,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Erreur API :", errorData.error);
      throw new Error(errorData.error || "Erreur lors de la mise à jour des données utilisateur.");
    }

    const updatedUserData = await response.json();
    setUserData(updatedUserData); // Mettre à jour le contexte utilisateur
    console.log("Données utilisateur mises à jour :", updatedUserData);

  } catch (error) {
    console.error("Erreur lors de la mise à jour des données utilisateur :", error);
  }
};


const getRanking = useCallback (async () => {
  try {
     // Obtenir le token ID pour l'authentification auprès de l'API
     const idToken = await auth.currentUser.getIdToken(/* forceRefresh */ true);

    const response = await fetch("https://api-secret-forsides.forsides-group.com/api/get-ranking", {
    // const response = await fetch("http://127.0.0.1:5000/api/get-ranking", {
      method: "GET",
      headers: { 
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
       },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Erreur lors de la récupération du classement.");
    }

    const {ranking, uid} = await response.json();
    console.log(uid)
    console.log(ranking)
    setRanking(ranking);
    localStorage.setItem("ranking", JSON.stringify(ranking));

    return { ranking, uid }

  } catch (error) {
    console.error("Erreur lors de la récupération du classement :", error);
    throw error;
  }
}, []);



// Synchroniser le contexte avec localStorage
useEffect(() => {
  const handleStorageChange = () => {
    const updatedData = JSON.parse(localStorage.getItem("userData"));
    setUserData(updatedData);
  };

  window.addEventListener("storage", handleStorageChange);
  return () => window.removeEventListener("storage", handleStorageChange);
}, []);

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        loginUser,
        logoutUser,
        openCase,
        updateUserDataQuizz,
        getRanking,
        ranking,
        statsQuizz,
        statsJour,
        isAuthenticated,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
